<script setup lang="ts">
/**
 * /alternatives/<slug> — one competitor's pricing, free plan and cancellation,
 * compared with ours on facts (see components/marketing/AlternativeGuide.vue
 * for the rules). Targets the brand queries: "<name>", "is <name> free",
 * "<name> pricing", "cancel <name> subscription", "<name> alternative".
 */
import { findCompetitor, withOurPrices } from '~/constants/competitors'
import { localizeCompetitor } from '~/constants/competitorsCs'

const { t, locale } = useI18n()
const route = useRoute()
const { faqPageNode } = useJsonLd()

definePageMeta({
  layout: false,
  auth: false,
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/alternativy/[slug]', de: '/alternativen/[slug]', pl: '/alternatywy/[slug]' } })

const found = findCompetitor(String(route.params.slug ?? ''))
if (!found) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

const competitor = computed(() => localizeCompetitor(found, locale.value))
const path = `/alternatives/${found.slug}`
const faqItems = computed(() => competitor.value.faq.map((item, i) => ({ id: String(i), question: item.q, answer: withOurPrices(item.a) })))
const extraNodes = computed(() => [faqPageNode(path, faqItems.value)])
</script>

<template>
  <MarketingFeaturePage
    :seo-title="competitor.seoTitle"
    :seo-description="competitor.seoDescription"
    :path="path"
    :breadcrumb-title="t('alternatives.page.heading', { name: competitor.name })"
    :parent="{ name: t('alternatives.hub.heading'), path: '/alternatives' }"
    :extra-nodes="extraNodes"
  >
    <MarketingAlternativeGuide :competitor="competitor" />

    <MarketingFaqSection
      :title="t('alternatives.page.faqTitle', { name: competitor.name })"
      :items="faqItems"
      :name="`alternative-${competitor.slug}-faq`"
    />
  </MarketingFeaturePage>
</template>
